// The home-page intro: a candle is lit and blown out, its smoke draws La Muerte's skull, marigold petals pour down
// and the page is revealed from the top (see IntroOverlay, smokeIntro and styles/intro.css).

/** How the petal shower is spread out in time (ms). */
export const SHOWER = Object.freeze({ maxDelay: 900, minDuration: 1900, durationSpread: 900 });

const PETALS_AT = 3200;

/** Milliseconds from the start of the intro. */
export const INTRO_TIMELINE = Object.freeze({
  candleOn: 150,
  lines: Object.freeze([400, 750]),
  candleOut: 1300,
  skullDraw: 1650,
  eyesOpen: 2750,
  skullBreath: 2900,
  petals: PETALS_AT,
  reveal: 3500,
  enter: 3600, // the hero starts its entrance (html[data-intro="entering"])
  revealEnd: 4400,
  done: 5400, // the entrance is over
  finish: PETALS_AT + SHOWER.maxDelay + SHOWER.minDuration + SHOWER.durationSpread, // the last petal has fallen
});

/** The hero title is typed one character per step (ms after the entrance starts), with a pause between words. */
export const TITLE_TYPING = Object.freeze({ start: 360, step: 70, wordGap: 240 });

/**
 * When each character of `words` appears and how long the cursor stays behind it. The very last character keeps
 * a blinking cursor instead (cursorFor: null).
 * @returns {Array<Array<{ char: string, at: number, cursorFor: number | null }>>}
 */
export const typingSchedule = (words, { start, step, wordGap } = TITLE_TYPING) => {
  const lengths = words.map((word) => [...word].length);
  const starts = lengths.reduce(
    (acc, _, i) => (i === 0 ? [start] : [...acc, acc[i - 1] + lengths[i - 1] * step + wordGap]),
    []
  );
  return words.map((word, w) =>
    [...word].map((char, i) => {
      const isLast = w === words.length - 1 && i === lengths[w] - 1;
      return { char, at: starts[w] + i * step, cursorFor: isLast ? null : step };
    })
  );
};

/** Only a page load that starts on the home route plays the intro, and never for visitors who prefer less motion. */
export const shouldPlayIntro = ({ pathname, prefersReducedMotion, canAnimate }) =>
  pathname === "/" && !prefersReducedMotion && canAnimate;

const MIN_PETALS = 20;
const MAX_PETALS = 44;
const PX_PER_PETAL = 40;

/** How many petals pour down over a window this wide. */
export const showerPetalCount = (width) =>
  Math.round(Math.min(MAX_PETALS, Math.max(MIN_PETALS, width / PX_PER_PETAL)));

/** Sets the intro phase on <html> ("playing" | "entering"), or clears it with null. */
export const setIntroPhase = (phase) => {
  const root = document.documentElement;
  if (phase) root.setAttribute("data-intro", phase);
  else root.removeAttribute("data-intro");
};

/** Resolves once `fonts` have loaded or `maxWait` ms have passed, whichever comes first. */
export const waitForFonts = (fonts, maxWait) => {
  if (typeof document.fonts?.load !== "function") return Promise.resolve();
  const loaded = Promise.all(fonts.map((font) => document.fonts.load(font))).catch(() => {});
  const timeout = new Promise((resolve) => setTimeout(resolve, maxWait));
  return Promise.race([loaded, timeout]);
};
